<template>
  <div v-if="showSkeleton" class="flex flex-col">
    <LcSkeleton class="mb-100" height="1.5rem" radius="medium" width="40%" />
    <LcSkeleton height="30rem" radius="medium" />
  </div>

  <div v-else-if="sections?.length" class="editor-faq-content flex flex-col gap-300">
    <section v-for="{ config, content } in sections" :key="config.name">
      <header class="flex items-center gap-100 pb-200">
        <LcIcon :name="config.icon" />

        <h3 class="paragraph-bold">
          {{ $t(`info.faq.section.${config.name}.title`) }}
        </h3>
      </header>

      <LcExpandableList>
        <LcExpandable
          v-for="item in content"
          :id="item.sys.id"
          :key="item.sys.id"
          content-class="pt-200"
          :model-value="false"
        >
          <template #header>{{ item.caption }}</template>

          <ContentfulRenderer class="content" :data="item.content" />
        </LcExpandable>
      </LcExpandableList>
    </section>
  </div>
</template>

<script lang="ts" setup>
import type { HighFrequencyGameId } from '@lottocom/high-frequency-game-mapping'

import type {
  HighFrequency_GameId,
  LotteryId,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { ContentBlock } from '#content/shared/@types/generated/cms/graphql-schema-types'

import { FAQ_COLLECTION_TYPES, getFaqCollectionType } from '#content/app/utils/faqCollection'

type Emits = {
  (e: 'error'): void
}

type Props = {
  lotteryId: HighFrequency_GameId | HighFrequencyGameId | LotteryId
}

const emit = defineEmits<Emits>()
const { lotteryId } = defineProps<Props>()

const toFaqBlock = (item: ContentBlock) => ({
  caption: item.caption || '',
  content: item.content,
  sys: { id: item.sys.id },
})

const COLLECTION_CONFIG = {
  [FAQ_COLLECTION_TYPES.PrizesAndWinnings]: {
    icon: 'winning',
    name: 'faqprizesandwinnings',
  },
  [FAQ_COLLECTION_TYPES.TicketsAndDrawings]: {
    icon: 'orders',
    name: 'faqticketsanddrawings',
  },
  [FAQ_COLLECTION_TYPES.TopQuestions]: {
    icon: 'star',
    name: 'faqtopquestions',
  },
} as const

const { data, error, pending } = useContent().getFaq(lotteryId)

const showSkeleton = computed(() => import.meta.client && pending.value)

const sections = computed(() => {
  if (!data.value) return

  const isCollection = ([key, value]: [string, any]) => key.includes('Collection') && !!value

  return Object.entries(data.value)
    .filter(isCollection)
    .map(([_, value]) => ({
      config: COLLECTION_CONFIG[getFaqCollectionType(value.__typename)],
      content: value.items.map(toFaqBlock),
    }))
})

watch(error, () => emit('error'))
</script>

<style lang="scss" scoped>
.editor-faq-content {
  :deep(.content) {
    dl,
    ol,
    ul {
      margin-bottom: token('size/space/gap/200');
    }

    ol,
    ul {
      padding-left: token('size/space/gap/200');
      list-style: unset;

      ::marker {
        color: token('color/text/accent/accent/placeholder');
      }
    }
  }
}
</style>
